(() => {
  if (customElements.get('alleycat-featured-scroll')) return;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const interactiveControls = 'button, input, select, textarea, summary, [contenteditable]:not([contenteditable="false"]), quantity-input, quantity-popover, .quantity, [role="spinbutton"], [role="slider"], [role="combobox"], [role="textbox"]';

  class AlleycatFeaturedScroll extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.rail = this.querySelector('[data-featured-scroll-rail]');
      this.products = this.querySelector('slider-component') || this.rail;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.controller = new AbortController();
      this.keyboardMode = true;
      this.pointer = null;
      this.touch = null;
      this.suppressClick = false;
      this.autoPosition = null;
      this.lastProgress = null;
      this.motionPosition = 0;
      this.needsRebase = true;
      this.frame = 0;
      this.needsLayout = true;
      this.classList.remove('is-dragging');
      this.classList.add('is-enhanced');
      const { signal } = this.controller;
      const schedule = () => this.schedule();
      const layout = () => { this.needsLayout = true; this.schedule(); };

      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', layout, { passive: true, signal });
      window.addEventListener('pageshow', layout, { signal });
      document.addEventListener('shopify:section:reorder', layout, { signal });
      this.reduced.addEventListener('change', () => this.rebase(), { signal });
      window.addEventListener('pointerdown', () => {
        const wasKeyboardFocused = this.isKeyboardFocused();
        this.keyboardMode = false;
        if (wasKeyboardFocused) this.rebase();
      }, { capture: true, signal });
      window.addEventListener('keydown', (event) => {
        if (['Alt', 'Control', 'Meta', 'Shift'].includes(event.key)) return;
        this.keyboardMode = true;
        if (this.isKeyboardFocused()) this.rebase();
      }, { capture: true, signal });

      if (this.rail) {
        this.rail.addEventListener('scroll', () => this.onRailScroll(), { passive: true, signal });
        this.rail.addEventListener('wheel', (event) => {
          if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || (event.shiftKey && event.deltaY)) this.rebase();
        }, { passive: true, signal });
        this.rail.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
        this.rail.addEventListener('pointermove', (event) => this.onPointerMove(event), { passive: false, signal });
        // Touch Events continue during native scrolling after pointercancel.
        // They record intent only; all touch gestures keep their native action.
        this.rail.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true, signal });
        this.rail.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: true, signal });
        this.rail.addEventListener('touchend', () => this.onTouchEnd(), { passive: true, signal });
        this.rail.addEventListener('touchcancel', () => this.onTouchEnd(), { passive: true, signal });
        window.addEventListener('pointerup', (event) => this.onPointerEnd(event), { signal });
        window.addEventListener('pointercancel', (event) => this.onPointerEnd(event), { signal });
        this.rail.addEventListener('lostpointercapture', (event) => this.onPointerEnd(event), { signal });
        this.rail.addEventListener('dragstart', (event) => {
          if (this.pointer?.type === 'mouse' && this.pointer.dragAllowed) event.preventDefault();
        }, { signal });
        this.rail.addEventListener('click', (event) => {
          // Keyboard activation has detail 0. Only the pointer click immediately
          // following a drag is suppressed; a new pointerdown clears this flag.
          if (!this.suppressClick || event.detail === 0) return;
          this.suppressClick = false;
          event.preventDefault();
          event.stopPropagation();
        }, { capture: true, signal });
        this.products.addEventListener('focusin', (event) => {
          this.rebase();
          if (this.isKeyboardFocused()) this.revealItem(event.target.closest('[data-featured-scroll-item]'));
        }, { signal });
        this.products.addEventListener('focusout', () => {
          if (this.keyboardMode) this.rebase();
        }, { signal });
        this.rail.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });
      }

      if (typeof ResizeObserver !== 'undefined') {
        this.observer = new ResizeObserver(layout);
        this.observer.observe(this);
        if (this.rail) {
          this.observer.observe(this.rail);
          this.rail.querySelectorAll('[data-featured-scroll-item]').forEach((item) => this.observer.observe(item));
        }
      }
      this.schedule();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.pointer = null;
      this.touch = null;
      this.classList.remove('is-enhanced', 'is-dragging', 'has-keyboard-focus');
    }

    schedule() {
      if (!this.controller || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (this.needsLayout) this.measure();
        this.updateMotion();
      });
    }

    measure() {
      this.needsLayout = false;
      this.needsRebase = true;
      this.maxScroll = Math.max(0, (this.rail?.scrollWidth || 0) - (this.rail?.clientWidth || 0));
      this.hasOverflow = this.maxScroll > 1;
      const item = this.rail?.querySelector('[data-featured-scroll-item]');
      const nextItem = item?.nextElementSibling;
      const gap = this.rail ? parseFloat(getComputedStyle(this.rail).columnGap) || 0 : 0;
      const itemDistance = nextItem ? nextItem.getBoundingClientRect().left - item.getBoundingClientRect().left : 0;
      this.step = itemDistance > 0 ? itemDistance : item ? item.getBoundingClientRect().width + gap : this.rail?.clientWidth || 0;
      if (this.rail) {
        this.rail.tabIndex = this.hasOverflow ? 0 : -1;
        this.rail.classList.toggle('has-overflow', this.hasOverflow);
      }
      // Focus may predate custom-element initialization, or the rail may only
      // start overflowing after a breakpoint change. In either case the
      // keyboard-focused product stays authoritative and is revealed locally.
      const focused = document.activeElement;
      if (this.isKeyboardFocused()) this.revealItem(focused.closest('[data-featured-scroll-item]'));
      this.updateEdges();
    }

    updateMotion() {
      if (!this.isConnected) return;
      const reduced = this.reduced.matches;
      const keyboardFocused = this.isKeyboardFocused();
      this.classList.toggle('has-keyboard-focus', Boolean(keyboardFocused));
      if (!this.rail || !this.products) return;
      const rect = this.products.getBoundingClientRect();
      const progress = clamp((innerHeight - rect.top) / (innerHeight + rect.height), 0, 1);
      const delta = this.lastProgress === null ? 0 : progress - this.lastProgress;
      this.lastProgress = progress;
      const strengthValue = innerWidth <= 749 ? this.dataset.sliderStrengthMobile ?? this.dataset.sliderStrength : this.dataset.sliderStrength;
      const strength = Math.max(0, Number(strengthValue) || 0) / 100;
      const paused = this.needsRebase || reduced || !strength || this.pointer || this.touch || keyboardFocused || !this.hasOverflow;
      this.needsRebase = false;
      if (paused) {
        this.motionPosition = this.rail.scrollLeft;
        return;
      }
      if (!delta) return;
      const travel = Math.min(this.maxScroll, this.rail.clientWidth * strength);
      // Add only NEW page-scroll travel to the user's current native offset.
      // Pausing, swiping, focusing, loading and resizing never jump to an
      // absolute timeline position. Beyond either edge no debt accumulates.
      this.motionPosition = clamp(this.motionPosition + delta * travel, 0, this.maxScroll);
      this.rail.scrollLeft = this.motionPosition;
      // Keep the browser's actual (possibly rounded) offset for diagnostics.
      // A scroll event alone is not evidence of user input: resize and scroll
      // anchoring can also change the offset after this write.
      this.autoPosition = this.rail.scrollLeft;
      this.updateEdges();
    }

    isKeyboardFocused() {
      return this.keyboardMode && this.products?.contains(document.activeElement);
    }

    rebase() {
      this.needsRebase = true;
      this.schedule();
    }

    onRailScroll() {
      // Native touch momentum, trackpad/scrollbar input and layout anchoring
      // all establish a fresh origin; none permanently disables the coupling.
      if (this.autoPosition === null || Math.abs(this.rail.scrollLeft - this.autoPosition) > 1) this.rebase();
      this.updateEdges();
    }

    updateEdges() {
      const left = this.rail?.scrollLeft || 0;
      this.classList.toggle('is-at-start', !this.hasOverflow || left <= 1);
      this.classList.toggle('is-at-end', !this.hasOverflow || left >= this.maxScroll - 1);
    }

    moveTo(left, smooth = true) {
      if (!this.rail || !this.hasOverflow) return;
      this.rebase();
      this.rail.scrollTo({ left: clamp(left, 0, this.maxScroll), behavior: smooth && !this.reduced.matches ? 'smooth' : 'instant' });
      this.updateEdges();
    }

    revealItem(item) {
      if (!item || !this.rail) return;
      const railRect = this.rail.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const styles = getComputedStyle(this.rail);
      const viewportLeft = railRect.left + this.rail.clientLeft;
      const first = this.rail.querySelector('[data-featured-scroll-item]');
      const last = this.rail.querySelector('[data-featured-scroll-item]:last-child');
      const leadingMargin = first ? Math.max(0, parseFloat(getComputedStyle(first).marginLeft) || 0) : 0;
      const trailingMargin = last ? Math.max(0, parseFloat(getComputedStyle(last).marginRight) || 0) : 0;
      const leftInset = Math.max(parseFloat(styles.scrollPaddingLeft) || 0, (parseFloat(styles.paddingLeft) || 0) + leadingMargin);
      const rightInset = Math.max(parseFloat(styles.scrollPaddingRight) || 0, (parseFloat(styles.paddingRight) || 0) + trailingMargin);
      const left = viewportLeft + leftInset;
      const right = viewportLeft + this.rail.clientWidth - rightInset;
      if (itemRect.left < left) this.moveTo(this.rail.scrollLeft + itemRect.left - left, false);
      else if (itemRect.right > right) this.moveTo(this.rail.scrollLeft + itemRect.right - right, false);
    }

    onKeyDown(event) {
      if (!this.hasOverflow || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.closest(interactiveControls)) return;
      const targets = { ArrowLeft: this.rail.scrollLeft - this.step, ArrowRight: this.rail.scrollLeft + this.step, Home: 0, End: this.maxScroll };
      if (!(event.key in targets)) return;
      event.preventDefault();
      this.moveTo(targets[event.key]);
    }

    onPointerDown(event) {
      this.suppressClick = false;
      if (!this.hasOverflow || event.isPrimary === false || event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      this.pointer = { id: event.pointerId, type: event.pointerType, x: event.clientX, y: event.clientY, left: this.rail.scrollLeft, axis: null, dragging: false, dragAllowed: !event.target.closest(interactiveControls) };
      // A deliberate press also covers the native scrollbar; actual dragging
      // still requires the direction/threshold check below.
      this.rebase();
    }

    onPointerMove(event) {
      const pointer = this.pointer;
      if (!pointer || pointer.id !== event.pointerId || !pointer.dragAllowed) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      if (!pointer.axis) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
        pointer.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (pointer.axis !== 'x') return;
      this.rebase();
      // Touch and pen use native scrolling and momentum. Never cancel their
      // events, so a vertical page gesture continues uninterrupted.
      if (pointer.type !== 'mouse') return;
      if (!pointer.dragging) {
        pointer.dragging = true;
        this.classList.add('is-dragging');
        this.rail.setPointerCapture?.(event.pointerId);
      }
      event.preventDefault();
      this.rail.scrollLeft = clamp(pointer.left - dx, 0, this.maxScroll);
      this.updateEdges();
    }

    onPointerEnd(event) {
      const pointer = this.pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      this.suppressClick = pointer.dragging && event.type !== 'pointercancel';
      this.pointer = null;
      this.classList.remove('is-dragging');
      if (this.rail.hasPointerCapture?.(event.pointerId)) this.rail.releasePointerCapture(event.pointerId);
      this.rebase();
    }

    onTouchStart(event) {
      if (!this.hasOverflow || event.touches.length !== 1) return;
      const touch = event.touches[0];
      this.touch = { x: touch.clientX, y: touch.clientY, left: this.rail.scrollLeft, axis: null };
      this.rebase();
    }

    onTouchMove(event) {
      if (!this.touch || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const dx = touch.clientX - this.touch.x;
      const dy = touch.clientY - this.touch.y;
      if (!this.touch.axis && Math.max(Math.abs(dx), Math.abs(dy)) >= 8) this.touch.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (this.touch.axis === 'x') this.rebase();
    }

    onTouchEnd() {
      this.touch = null;
      this.rebase();
    }
  }

  customElements.define('alleycat-featured-scroll', AlleycatFeaturedScroll);
})();